import React from 'react';
import { visitEditPath } from '@app/features/dashboard/visitLinks';
import { VisitNotesSection } from '@app/features/notes';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import { Button, Descriptions, Drawer, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDate, formatTime } from '../clientCard.format';
import {
  CardVisit,
  CARD_VITALS,
  FolderFile,
  folderBucketOf,
  visitCaregiverNames,
  visitDurationMinutes,
  visitLateMinutes,
  visitServiceLines,
  visitStatusLabelKey,
} from '../clientCard.model';
import { FileRow } from '../ClientFolderPanel';

export const cardDrawerWidth = (max: number): number => Math.min(max, window.innerWidth - 40);

type VisitDrawerProps = {
  visit: CardVisit | null;
  /** The card's client. A visit carries no client id of its own in this payload. */
  clientId: number;
  onClose: () => void;
  onOpenFile: (file: FolderFile) => void;
};

/**
 * One visit in full: what was done, by whom, what it cost, what was measured, what
 * was written and what was attached.
 *
 * Read-only. Editing a visit is the visit form's job, and the footer links there
 * rather than reproducing it — one screen that can change a visit is enough.
 */
const VisitDrawer: React.FC<VisitDrawerProps> = ({ visit, clientId, onClose, onOpenFile }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const services = visit ? visitServiceLines(visit) : [];
  const caregivers = visit ? visitCaregiverNames(visit) : [];
  const late = visit ? visitLateMinutes(visit) : null;
  const measured = visit ? CARD_VITALS.filter((vital) => typeof visit[vital.key] === 'number') : [];

  const files: FolderFile[] = visit
    ? (visit.attachments ?? []).map((attachment) => ({
        attachment,
        source: { type: 'visit', visitId: visit.id },
        at: attachment.uploadedAt,
        bucket: folderBucketOf(attachment),
      }))
    : [];

  return (
    <Drawer
      open={!!visit}
      onClose={onClose}
      width={cardDrawerWidth(560)}
      title={
        visit ? `${formatDate(visit.startTime, i18n.language)} · ${formatTime(visit.startTime, i18n.language)}` : ''
      }
      footer={
        visit && (
          <Button type="primary" onClick={() => navigate(visitEditPath(visit.id))}>
            {t('client-card-open-visit')}
          </Button>
        )
      }
    >
      {visit && (
        <div className="flex flex-col gap-4">
          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              {
                key: 'services',
                label: t('client-card-visit-services'),
                children: (
                  <div className="flex flex-col gap-1">
                    {services.length === 0 ? (
                      <span className="text-slate-400">{t('client-card-visit-no-services')}</span>
                    ) : (
                      services.map((service) => (
                        <div key={service.id} className="flex justify-between gap-3">
                          <span>{service.name}</span>
                          <span className="shrink-0 tabular-nums text-slate-400">
                            {t('client-card-minutes', { minutes: service.minutes })}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                ),
              },
              {
                key: 'duration',
                label: t('client-card-visit-duration'),
                children: [
                  t('client-card-minutes', { minutes: visitDurationMinutes(visit) }),
                  late === null ? null : t('client-card-visit-late-long', { minutes: late }),
                ]
                  .filter(Boolean)
                  .join(' · '),
              },
              {
                key: 'caregivers',
                label: t('client-card-visit-caregivers'),
                children: caregivers.join(', ') || t('dashboard-note-unassigned-caregiver'),
              },
              {
                key: 'status',
                label: t('client-card-visit-status'),
                children: <Tag className="!mr-0">{t(visitStatusLabelKey(visit))}</Tag>,
              },
              ...(typeof visit.priceTotal === 'number'
                ? [
                    {
                      key: 'price',
                      label: t('client-card-visit-price'),
                      children: currencyFormatter(visit.priceTotal, undefined, i18n.language),
                    },
                  ]
                : []),
            ]}
          />

          {measured.length > 0 && (
            <div>
              <div className="mb-1 text-[13px] font-semibold text-slate-700">{t('client-card-visit-vitals')}</div>
              <Descriptions
                column={2}
                size="small"
                bordered
                items={measured.map((vital) => ({
                  key: vital.key,
                  label: t(vital.labelKey),
                  children: (
                    <span className="tabular-nums">
                      {visit[vital.key] as number} {vital.unit ?? ''}
                    </span>
                  ),
                }))}
              />
            </div>
          )}

          {/* Every note on the visit, with its author and time. There is no second,
              visit-derived note above it any more: that was one field holding the newest
              caregiver note, and this is the stream it came from. */}
          <VisitNotesSection visitId={visit.id} clientId={clientId} />

          {files.length > 0 && (
            <div>
              <div className="mb-1 text-[13px] font-semibold text-slate-700">
                {t('client-card-visit-attachments', { count: files.length })}
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                {files.map((file) => (
                  <FileRow key={file.attachment.attachmentId} file={file} onOpen={onOpenFile} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default VisitDrawer;
